import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { iosNativeContent, iosNativePageRoot } from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Apple, ChefHat, ArrowLeft, Search, Filter } from 'lucide-react';
import type { DietPlan, ScanAnalysis, DietFoodItem } from '../services/scanService';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { filterCuisineFoodsByScan } from '../services/cuisineFilterService';

/** Create minimal recipe fallback so every food shows View Recipe */
function getRecipeForDisplay(food: { name: string; description?: string; recipe?: { title?: string; ingredients?: string[]; steps?: string[]; instructions?: string[]; servings?: number; caloriesPerServing?: number } }) {
  if (food.recipe && (food.recipe.ingredients?.length || food.recipe.instructions?.length || food.recipe.steps?.length)) {
    return {
      title: food.recipe.title || food.name,
      ingredients: food.recipe.ingredients || food.recipe.instructions || [],
      steps: food.recipe.steps || food.recipe.instructions || ['Prepare according to the description above.', 'Use whole grains and vegetables where possible.'],
      servings: food.recipe.servings ?? 2,
      caloriesPerServing: food.recipe.caloriesPerServing ?? 350,
    };
  }
  return {
    title: food.name,
    ingredients: ['Whole grains or vegetable base', 'Fresh vegetables', 'Olive oil or healthy fat', 'Herbs and spices', 'Salt and pepper to taste'],
    steps: ['Review the description above for key ingredients.', 'Use whole grains and vegetables where possible.', 'Prepare using baking, grilling, or steaming instead of frying.', 'Season to taste and serve.'],
    servings: 2,
    caloriesPerServing: 350,
  };
}

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem 4rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;

  ${iosNativeContent}
`;

const Header = styled.div`
  margin-bottom: 2rem;
  animation: slideUp 0.5s ease-out;
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  margin-top: 0.5rem;
`;

const BackButton = styled(Button)`
  margin-bottom: 1.5rem;
`;

const FoodsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const FoodCard = styled(Card)`
  padding: 1.5rem;
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
`;

const FoodName = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FoodDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin: 0 0 1rem 0;
  line-height: 1.6;
`;

const RecipeSection = styled.details`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${theme.colors.border.light};
`;

const RecipeSummary = styled.summary`
  cursor: pointer;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary[600]};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    color: ${theme.colors.primary[700]};
  }
`;

const RecipeContent = styled.div`
  margin-top: 1rem;
  padding-left: 1rem;
`;

const RecipeInfo = styled.p`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  margin: 0 0 0.75rem 0;
`;

const RecipeSectionTitle = styled.strong`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  display: block;
  margin-bottom: 0.5rem;
`;

const IngredientsList = styled.ul`
  margin: 0.5rem 0 1rem 0;
  padding-left: 1.5rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
`;

const StepsList = styled.ol`
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
`;

const StepItem = styled.li`
  margin-bottom: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${theme.colors.text.secondary};
`;

const EmptyStateTitle = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  margin-bottom: 1.5rem;
`;

const FilterSection = styled.div`
  margin-bottom: 2rem;
  opacity: 1;
  will-change: auto;
  transform: translateZ(0);
  backface-visibility: hidden;
`;

const FilterLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 1rem;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  will-change: auto;

  svg {
    color: ${theme.colors.primary[600]};
    filter: none;
  }
`;

const SearchInput = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 500px;
  background: #ffffff;
  border: 2px solid ${theme.colors.border.medium || theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  padding: 0.75rem 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  will-change: auto;
  backface-visibility: hidden;
  transform: translateZ(0);
  opacity: 1;

  &:focus-within {
    border-color: ${theme.colors.primary[600]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
    outline: none;
  }

  svg {
    color: ${theme.colors.primary[600]};
    margin-right: 0.75rem;
    flex-shrink: 0;
    filter: none;
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.medium};
    color: #1a1a1a;
    background: transparent;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    letter-spacing: 0.01em;
    will-change: auto;
    filter: none;

    &::placeholder {
      color: #6b7280;
      font-weight: ${theme.typography.fontWeight.normal};
      opacity: 1;
    }

    &:focus {
      color: #1a1a1a;
      filter: none;
    }
  }
`;

const ClearButton = styled.button`
  background: ${theme.colors.primary[100]};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: 0.25rem 0.75rem;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary[700]};
  cursor: pointer;
  transition: background-color 0.2s ease;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  margin-left: 0.5rem;
  will-change: auto;
  filter: none;

  &:hover {
    background: ${theme.colors.primary[200]};
  }
`;

// Helper function to generate additional skin-friendly meals and snacks
// Personalized based on user's skin health scores and results
const generateAdditionalSkinFriendlyFoods = (
  scanAnalysis: ScanAnalysis | undefined,
  dietaryPreference?: 'vegetarian' | 'non-vegetarian',
  neededMeals: number = 0,
  neededSnacks: number = 0
): DietFoodItem[] => {
  const additionalFoods: DietFoodItem[] = [];
  const isVegetarian = dietaryPreference === 'vegetarian';
  const isNonVegetarian = dietaryPreference === 'non-vegetarian';
  const includeBoth = !dietaryPreference; // When not set, include both
  
  // Get scan parameters for personalization
  const glowScore = scanAnalysis?.skinHealthIndex || 70;
  const acneSeverity = scanAnalysis?.acneAnalysis?.overallSeverity || 'mild';
  const estimatedPH = scanAnalysis?.acneAnalysis?.estimatedPH;
  const isInflammatory = scanAnalysis?.acneAnalysis?.acneType?.inflammatory === 'moderate' || 
                         scanAnalysis?.acneAnalysis?.acneType?.inflammatory === 'pronounced';
  
  // Determine if user needs anti-inflammatory foods (low scores, bad skin)
  const needsAntiInflammatory = isInflammatory || acneSeverity === 'moderate' || 
                                acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe';
  
  // Determine if user needs high omega-3 foods (low scores, bad skin)
  const needsOmega3 = glowScore < 70 || needsAntiInflammatory;
  
  // Determine if user needs high antioxidant foods (low scores, bad skin)
  const needsAntioxidants = glowScore < 75 || (estimatedPH && estimatedPH > 5.5);
  
  // Determine score-based food pool
  // Low scores (0-60): needs more healing foods (anti-inflammatory, omega-3, antioxidants)
  // Moderate scores (60-75): balanced healing and maintenance
  // High scores (75-100): maintenance foods
  const scoreRange = glowScore < 60 ? 'low' : glowScore < 75 ? 'moderate' : 'high';
  const isLowScore = glowScore < 60;
  const isModerateScore = glowScore >= 60 && glowScore < 75;
  const isHighScore = glowScore >= 75;
  
  // Generate personalized meals based on skin health
  // For LOW SCORES + BAD SKIN: Focus on anti-inflammatory, omega-3, high antioxidant foods
  // For HIGH SCORES + GOOD SKIN: Focus on maintenance and balanced nutrition
  const vegetarianMeals: DietFoodItem[] = [
    // Low score meals (healing focus)
    ...(isLowScore || needsAntiInflammatory ? [
      {
        type: 'meal' as const,
        name: 'Turmeric and Ginger Lentil Curry with Brown Rice',
        description: 'High in anti-inflammatory spices (turmeric, ginger) and protein. Specifically recommended for reducing inflammation and supporting skin healing.',
        recipe: {
          title: 'Turmeric and Ginger Lentil Curry',
          ingredients: [
            '1/2 cup red lentils',
            '1/2 cup cooked brown rice',
            '1 cup mixed vegetables (cauliflower, carrots)',
            '1 tbsp olive oil',
            '1 tsp turmeric powder',
            '1 inch fresh ginger, grated',
            '2 cloves garlic, minced',
            '1 tsp cumin',
            'Fresh cilantro',
            'Salt to taste'
          ],
          steps: [
            'Cook brown rice according to package directions.',
            'Rinse lentils and cook in 2 cups water until soft (20-25 minutes).',
            'Heat oil, add cumin seeds until they splutter.',
            'Add garlic and ginger, sauté for 30 seconds.',
            'Add turmeric and vegetables, cook for 5 minutes.',
            'Add cooked lentils, simmer for 10 minutes.',
            'Season with salt, serve over brown rice.',
            'Garnish with fresh cilantro.'
          ],
          servings: 1,
          caloriesPerServing: 400
        }
      },
      {
        type: 'meal' as const,
        name: 'Quinoa and Black Bean Bowl with Antioxidant-Rich Vegetables',
        description: 'High in antioxidants (bell peppers, tomatoes) and complete protein. Supports skin barrier repair and reduces oxidative stress.',
        recipe: {
          title: 'Quinoa and Black Bean Bowl',
          ingredients: [
            '1/2 cup cooked quinoa',
            '1/2 cup black beans (canned, drained)',
            '1 cup mixed vegetables (bell peppers, tomatoes, spinach)',
            '1 tbsp olive oil',
            '1/4 avocado, sliced',
            '1 tsp lime juice',
            'Fresh cilantro',
            'Salt and pepper to taste'
          ],
          steps: [
            'Cook quinoa according to package directions.',
            'Roast vegetables with olive oil at 400°F for 20 minutes.',
            'Heat black beans.',
            'Layer quinoa, beans, and vegetables in a bowl.',
            'Top with avocado, drizzle with lime juice.',
            'Garnish with cilantro and serve.'
          ],
          servings: 1,
          caloriesPerServing: 380
        }
      }
    ] : []),
    // Moderate score meals (balanced)
    ...(isModerateScore ? [
      {
        type: 'meal' as const,
        name: 'Lentil and Vegetable Soup with Whole Grain Bread',
        description: 'High-protein soup with fiber and anti-inflammatory properties. Supports skin barrier health and balanced nutrition.',
        recipe: {
          title: 'Lentil and Vegetable Soup',
          ingredients: [
            '1/2 cup red lentils',
            '1 cup mixed vegetables (carrots, celery, tomatoes)',
            '2 cups vegetable broth',
            '1 tbsp olive oil',
            '2 cloves garlic, minced',
            '1 tsp turmeric',
            'Fresh parsley',
            'Salt and pepper to taste'
          ],
          steps: [
            'Heat olive oil, sauté garlic and vegetables for 5 minutes.',
            'Add lentils, broth, and turmeric.',
            'Simmer for 20-25 minutes until lentils are soft.',
            'Season with salt and pepper.',
            'Garnish with parsley and serve with whole grain bread.'
          ],
          servings: 1,
          caloriesPerServing: 320
        }
      }
    ] : []),
    // High score meals (maintenance)
    ...(isHighScore ? [
      {
        type: 'meal' as const,
        name: 'Tofu Scramble with Vegetables and Whole Grain Toast',
        description: 'High-protein breakfast with vegetables and whole grains. Maintains skin health and provides essential nutrients.',
        recipe: {
          title: 'Tofu Scramble',
          ingredients: [
            '4 oz firm tofu, crumbled',
            '1 cup mixed vegetables (spinach, bell peppers, mushrooms)',
            '1 slice whole grain bread',
            '1 tbsp olive oil',
            '1 tsp turmeric',
            '1/2 tsp cumin',
            'Salt and pepper to taste'
          ],
          steps: [
            'Toast whole grain bread.',
            'Heat olive oil, crumble and cook tofu with turmeric and cumin.',
            'Add vegetables and cook until tender.',
            'Season with salt and pepper.',
            'Serve with toast.'
          ],
          servings: 1,
          caloriesPerServing: 310
        }
      }
    ] : []),
    // Always include at least one meal
    {
      type: 'meal' as const,
      name: 'Chickpea and Vegetable Stir-Fry with Quinoa',
      description: needsOmega3 
        ? 'Plant-based protein with whole grains. High in fiber and zinc, supports skin repair and reduces inflammation.'
        : 'Plant-based protein with whole grains. Supports stable blood sugar and skin health.',
      recipe: {
        title: 'Chickpea and Vegetable Stir-Fry',
        ingredients: [
          '1/2 cup cooked quinoa',
          '1/2 cup chickpeas (canned, drained)',
          '1 cup mixed vegetables (bell peppers, broccoli, snap peas)',
          '1 tbsp olive oil',
          '2 cloves garlic, minced',
          '1 tsp ginger, grated',
          '2 tbsp low-sodium soy sauce',
          'Salt and pepper to taste'
        ],
        steps: [
          'Cook quinoa according to package directions.',
          'Heat olive oil, pan-fry chickpeas until slightly crispy (5 minutes).',
          'Add garlic and ginger, stir for 30 seconds.',
          'Add vegetables, stir-fry for 4-5 minutes.',
          'Add soy sauce, toss to combine.',
          'Serve over quinoa.'
        ],
        servings: 1,
        caloriesPerServing: 360
      }
    }
  ];
  
  const nonVegetarianMeals: DietFoodItem[] = [
    // Non-vegetarian meals
    // Low score meals (healing focus - omega-3, anti-inflammatory)
    ...(isLowScore || needsOmega3 ? [
      {
        type: 'meal' as const,
        name: 'Baked Salmon with Sweet Potato and Steamed Broccoli',
        description: 'Omega-3 rich meal specifically for reducing inflammation and supporting skin barrier repair. Ideal for low skin scores.',
        recipe: {
          title: 'Baked Salmon with Sweet Potato',
          ingredients: [
            '1 salmon fillet (6 oz)',
            '1 medium sweet potato, baked',
            '1 cup broccoli, steamed',
            '1 tbsp olive oil',
            '1 lemon',
            'Fresh dill',
            'Salt and pepper to taste'
          ],
          steps: [
            'Bake sweet potato at 400°F for 45 minutes.',
            'Season salmon with salt, pepper, and lemon juice.',
            'Bake salmon for 12-15 minutes until flaky.',
            'Steam broccoli until tender.',
            'Serve salmon with sweet potato and broccoli, garnish with dill.'
          ],
          servings: 1,
          caloriesPerServing: 450
        }
      },
      {
        type: 'meal' as const,
        name: 'Grilled Salmon with Quinoa and Antioxidant-Rich Vegetables',
        description: 'High omega-3 content reduces inflammation. Rich in antioxidants from vegetables. Specifically recommended for improving skin health.',
        recipe: {
          title: 'Grilled Salmon with Quinoa',
          ingredients: [
            '1 salmon fillet (6 oz)',
            '1/2 cup cooked quinoa',
            '1 cup mixed vegetables (bell peppers, tomatoes, spinach)',
            '1 tbsp olive oil',
            '1 lemon',
            '2 cloves garlic, minced',
            'Fresh herbs (dill or parsley)',
            'Salt and pepper to taste'
          ],
          steps: [
            'Cook quinoa according to package directions.',
            'Season salmon with salt, pepper, and lemon juice.',
            'Grill or pan-sear salmon for 4-5 minutes per side until flaky.',
            'Sauté vegetables with garlic and olive oil.',
            'Serve salmon over quinoa with vegetables. Garnish with fresh herbs.'
          ],
          servings: 1,
          caloriesPerServing: 480
        }
      }
    ] : []),
    // Moderate score meals (balanced)
    ...(isModerateScore ? [
      {
        type: 'meal' as const,
        name: 'Grilled Chicken Breast with Quinoa and Steamed Broccoli',
        description: 'Lean protein with whole grains and vegetables. High in protein and supports skin repair.',
        recipe: {
          title: 'Grilled Chicken with Quinoa',
          ingredients: [
            '1 chicken breast (6 oz)',
            '1/2 cup cooked quinoa',
            '1 cup broccoli, steamed',
            '1 tbsp olive oil',
            '1 lemon',
            '2 cloves garlic, minced',
            'Salt and pepper to taste'
          ],
          steps: [
            'Cook quinoa according to package directions.',
            'Season chicken with garlic, salt, pepper, and lemon juice.',
            'Grill or pan-sear chicken for 5-6 minutes per side.',
            'Steam broccoli until tender.',
            'Serve chicken over quinoa with broccoli.'
          ],
          servings: 1,
          caloriesPerServing: 420
        }
      }
    ] : []),
    // High score meals (maintenance)
    ...(isHighScore ? [
      {
        type: 'meal' as const,
        name: 'Turkey and Vegetable Skewers with Brown Rice',
        description: 'Lean protein with vegetables and whole grains. Maintains skin health and provides balanced nutrition.',
        recipe: {
          title: 'Turkey Skewers with Brown Rice',
          ingredients: [
            '4 oz turkey breast, cubed',
            '1/2 cup cooked brown rice',
            '1 cup mixed vegetables (bell peppers, zucchini, onions)',
            '1 tbsp olive oil',
            '2 cloves garlic, minced',
            '1 tsp rosemary',
            'Salt and pepper to taste'
          ],
          steps: [
            'Marinate turkey with olive oil, garlic, rosemary, salt, and pepper.',
            'Thread turkey and vegetables onto skewers.',
            'Grill or broil for 8-10 minutes, turning occasionally.',
            'Cook brown rice according to package directions.',
            'Serve skewers over brown rice.'
          ],
          servings: 1,
          caloriesPerServing: 400
        }
      }
    ] : []),
    // Always include at least one meal
    {
      type: 'meal' as const,
      name: 'Baked Cod with Sweet Potato and Steamed Vegetables',
      description: needsAntiInflammatory
        ? 'Lean white fish with anti-inflammatory properties. Complex carbs and cruciferous vegetables support skin healing.'
        : 'Lean white fish with complex carbs and vegetables. Supports skin health.',
      recipe: {
        title: 'Baked Cod with Sweet Potato',
        ingredients: [
          '1 cod fillet (6 oz)',
          '1 medium sweet potato, baked',
          '1 cup mixed vegetables (broccoli, carrots)',
          '1 tbsp olive oil',
          '1 lemon',
          'Fresh dill',
          'Salt and pepper to taste'
        ],
        steps: [
          'Bake sweet potato at 400°F for 45 minutes.',
          'Season cod with salt, pepper, and lemon juice.',
          'Bake cod for 12-15 minutes until flaky.',
          'Steam vegetables until tender.',
          'Serve cod with sweet potato and vegetables, garnish with dill.'
        ],
        servings: 1,
        caloriesPerServing: 360
      }
    }
  ];
  
  // Combine meals based on dietary preference
  // Vegetarian: only vegetarian. Non-vegetarian: BOTH veg and non-veg (mix). Unset: mix.
  let combinedMeals: DietFoodItem[] = [];
  if (isVegetarian) {
    combinedMeals = vegetarianMeals;
  } else if (isNonVegetarian || includeBoth) {
    // Non-vegetarian or unset: provide a mix of vegetarian and non-vegetarian
    const maxMealsPerType = Math.ceil(neededMeals / 2);
    combinedMeals = [
      ...vegetarianMeals.slice(0, maxMealsPerType),
      ...nonVegetarianMeals.slice(0, maxMealsPerType)
    ];
  }
  
  // Generate personalized snacks based on skin health
  const personalizedSnacks: DietFoodItem[] = [
    // Low score snacks (healing focus - antioxidants, omega-3)
    ...(isLowScore || needsAntioxidants ? [
      {
        type: 'snack' as const,
        name: 'Greek Yogurt with Berries and Walnuts',
        description: 'High in antioxidants (berries) and omega-3 (walnuts). Probiotics support gut-skin connection. Specifically recommended for low skin scores.',
        recipe: {
          title: 'Greek Yogurt with Berries and Walnuts',
          ingredients: [
            '1/2 cup plain Greek yogurt',
            '1/2 cup mixed berries (blueberries, strawberries)',
            '1 tbsp chopped walnuts',
            '1 tsp honey (optional)'
          ],
          steps: [
            'Scoop Greek yogurt into a bowl.',
            'Top with fresh berries and walnuts.',
            'Drizzle with honey if desired.',
            'Enjoy as a healing snack.'
          ],
          servings: 1,
          caloriesPerServing: 200
        }
      },
      {
        type: 'snack' as const,
        name: 'Green Tea with Lemon and Antioxidant-Rich Snack',
        description: 'Rich in antioxidants (EGCG from green tea) that reduce inflammation. Best for low skin scores and inflammatory acne.',
        recipe: {
          title: 'Antioxidant Green Tea',
          ingredients: [
            '1 green tea bag or 1 tsp loose green tea',
            '1 cup hot water (175-185°F)',
            '1 tsp honey (optional)',
            'Lemon slice',
            '1/4 cup mixed berries (optional)'
          ],
          steps: [
            'Heat water to 175-185°F (not boiling).',
            'Steep green tea for 2-3 minutes.',
            'Remove tea bag or strain.',
            'Add honey and lemon if desired.',
            'Serve with berries if desired.',
            'Drink 2-3 cups daily for maximum benefits.'
          ],
          servings: 1,
          caloriesPerServing: 10
        }
      }
    ] : []),
    // Moderate/High score snacks (maintenance)
    ...(isModerateScore || isHighScore ? [
      {
        type: 'snack' as const,
        name: 'Greek Yogurt with Berries and Almonds',
        description: 'Protein-rich snack with antioxidants. Supports gut health and provides vitamin C for collagen production.',
        recipe: {
          title: 'Greek Yogurt with Berries',
          ingredients: [
            '1/2 cup plain Greek yogurt',
            '1/2 cup mixed berries (blueberries, strawberries)',
            '1 tbsp sliced almonds',
            '1 tsp honey (optional)'
          ],
          steps: [
            'Scoop Greek yogurt into a bowl.',
            'Top with fresh berries and almonds.',
            'Drizzle with honey if desired.',
            'Enjoy as a healthy snack.'
          ],
          servings: 1,
          caloriesPerServing: 180
        }
      }
    ] : []),
    // Always include these snacks
    {
      type: 'snack' as const,
      name: 'Hummus with Fresh Vegetables',
      description: needsAntioxidants
        ? 'Protein-rich snack with fiber and healthy fats. High in antioxidants from vegetables. Supports gut health and skin barrier repair.'
        : 'Protein-rich snack with fiber and healthy fats. Supports gut health and skin barrier.',
      recipe: {
        title: 'Hummus with Vegetables',
        ingredients: [
          '1/2 cup hummus',
          '1 cup fresh vegetables (carrot sticks, cucumber slices, bell pepper strips)',
          '1 tsp lemon juice'
        ],
        steps: [
          'Serve hummus in a bowl.',
          'Arrange fresh vegetables around the hummus.',
          'Enjoy as a healthy snack.'
        ],
        servings: 1,
        caloriesPerServing: 220
      }
    },
    {
      type: 'snack' as const,
      name: 'Apple Slices with Almond Butter',
      description: needsOmega3
        ? 'Fiber-rich snack with healthy fats. Supports stable blood sugar and provides omega-3 from almonds. Ideal for skin healing.'
        : 'Fiber-rich snack with healthy fats and antioxidants. Supports stable blood sugar and skin health.',
      recipe: {
        title: 'Apple with Almond Butter',
        ingredients: [
          '1 medium apple, sliced',
          '2 tbsp almond butter',
          '1 tsp chia seeds (optional)'
        ],
        steps: [
          'Slice apple into wedges.',
          'Serve with almond butter for dipping.',
          'Sprinkle with chia seeds if desired.',
          'Enjoy as a satisfying snack.'
        ],
        servings: 1,
        caloriesPerServing: 280
      }
    }
  ];
  
  const mealsToAdd = combinedMeals.slice(0, neededMeals);
  const snacksToAdd = personalizedSnacks.slice(0, neededSnacks);
  return [...mealsToAdd, ...snacksToAdd];
};

const DietPlanFoodsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const userProfile = auth?.userProfile;
  const { tier } = useSubscription();
  const { dietPlan, scanAnalysis, upfrontFoods } = (location.state as { 
    dietPlan?: DietPlan;
    scanAnalysis?: ScanAnalysis;
    upfrontFoods?: Array<{ name: string }>;
  }) || {};
  const [cuisineSearch, setCuisineSearch] = useState('');
  const [loadingCuisine, setLoadingCuisine] = useState(false);
  const [cuisineError, setCuisineError] = useState<string | null>(null);


  
  // Filter out any upfront foods to prevent duplicates
  const upfrontFoodNames = new Set(upfrontFoods?.map(f => f.name) || []);
  const filteredDietPlan = dietPlan ? {
      ...dietPlan,
      foodsToEat: dietPlan.foodsToEat.filter(food => !upfrontFoodNames.has(food.name))
  } : undefined;

  // Use filtered diet plan (excluding upfront foods)
  const displayDietPlan = filteredDietPlan || dietPlan;

  // State for ChatGPT-filtered cuisine foods
  const [chatGPTFoods, setChatGPTFoods] = useState<DietFoodItem[]>([]);
  const cuisineSearchRef = useRef(cuisineSearch);

  useEffect(() => {
    cuisineSearchRef.current = cuisineSearch;
  }, [cuisineSearch]);

  // Use ChatGPT API to filter cuisine foods when cuisine search is active
  useEffect(() => {
    const searchTerm = cuisineSearch.toLowerCase().trim();

    // Check if it's a cuisine search
    const worldCuisines = [
      'indian', 'chinese', 'japanese', 'french', 'mediterranean', 'american',
      'italian', 'mexican', 'thai', 'korean', 'greek', 'spanish', 'middle eastern',
      'swedish', 'norwegian', 'danish', 'finnish', 'icelandic', 'german', 'austrian', 'swiss',
      'belgian', 'dutch', 'polish', 'czech', 'hungarian', 'romanian', 'bulgarian', 'croatian',
      'serbian', 'slovak', 'slovenian', 'ukrainian', 'russian', 'turkish', 'lebanese', 'syrian',
      'jordanian', 'israeli', 'palestinian', 'egyptian', 'moroccan', 'tunisian', 'algerian',
      'ethiopian', 'kenyan', 'south african', 'nigerian', 'ghanaian', 'senegalese',
      'vietnamese', 'cambodian', 'laotian', 'myanmar', 'malaysian', 'singaporean',
      'indonesian', 'filipino', 'mongolian', 'taiwanese', 'hong kong',
      'guatemalan', 'honduran', 'salvadoran', 'nicaraguan', 'costa rican',
      'panamanian', 'colombian', 'venezuelan', 'ecuadorian', 'peruvian', 'bolivian',
      'chilean', 'argentine', 'uruguayan', 'paraguayan', 'brazilian',
      'australian', 'new zealand', 'fijian', 'polynesian', 'hawaiian',
      'portuguese', 'albanian', 'macedonian', 'georgian', 'armenian', 'azerbaijani',
      'iranian', 'afghan', 'pakistani', 'bangladeshi', 'sri lankan', 'nepalese', 'bhutanese', 'tibetan'
    ];

    const isCuisineSearch = searchTerm.length >= 2 && (
      worldCuisines.some(cuisine => searchTerm.includes(cuisine) || cuisine.includes(searchTerm)) ||
      /^[a-z]{3,}$/.test(searchTerm)
    );

    if (isCuisineSearch && scanAnalysis && searchTerm.length >= 3) {
      const matchedCuisine = worldCuisines.find(c =>
        searchTerm.includes(c) || c.includes(searchTerm)
      ) || searchTerm;

      // Clear immediately so we never show a different cuisine while loading
      setChatGPTFoods([]);
      setCuisineError(null);
      setLoadingCuisine(true);

      const timeoutId = setTimeout(async () => {
        const cuisineAtRequest = matchedCuisine;
        try {
          const recommendations = await filterCuisineFoodsByScan(
            cuisineAtRequest,
            scanAnalysis,
            userProfile?.dietaryPreference
          );

          // Only update if search hasn't changed (avoid race: Indian request returns after user switched to Chinese)
          const currentTerm = cuisineSearchRef.current.toLowerCase().trim();
          const currentMatch = worldCuisines.find(c =>
            currentTerm.includes(c) || c.includes(currentTerm)
          ) || currentTerm;
          if (currentMatch !== cuisineAtRequest) return;

          const convertedFoods: DietFoodItem[] = recommendations.map(rec => ({
            name: rec.name,
            description: rec.description,
            type: rec.type,
            recipe: rec.recipe ? {
              title: rec.name,
              ingredients: rec.recipe.ingredients,
              steps: rec.recipe.instructions,
              servings: rec.recipe.servings,
              caloriesPerServing: rec.recipe.caloriesPerServing,
            } : undefined,
          }));

          setChatGPTFoods(convertedFoods);
        } catch (error: unknown) {
          const currentTerm = cuisineSearchRef.current.toLowerCase().trim();
          const currentMatch = worldCuisines.find(c =>
            currentTerm.includes(c) || c.includes(currentTerm)
          ) || currentTerm;
          if (currentMatch !== cuisineAtRequest) return;

          const msg = error instanceof Error ? error.message : 'Failed to load cuisine recommendations.';
          console.error('Error filtering cuisine foods with ChatGPT:', error);
          setCuisineError(msg);
          setChatGPTFoods([]);
        } finally {
          setLoadingCuisine(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setChatGPTFoods([]);
      setLoadingCuisine(false);
      setCuisineError(null);
    }
  }, [cuisineSearch, scanAnalysis, userProfile?.dietaryPreference]);
  
  if (!displayDietPlan || !displayDietPlan.foodsToEat || displayDietPlan.foodsToEat.length === 0) {
    return (
      <PageContainer>
        <Container>
          <BackButton
            variant="outline"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} />
            Back
          </BackButton>
          <EmptyState>
            <EmptyStateTitle>No Diet Plan Found</EmptyStateTitle>
            <EmptyStateText>
              We couldn't find the diet plan. Please go back and try again.
            </EmptyStateText>
            <Button
              variant="primary"
              onClick={() => navigate('/diet')}
            >
              Go to Diet Page
            </Button>
          </EmptyState>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <BackButton
          variant="outline"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} />
          Back
        </BackButton>

        <Header>
          <Title>
            <Apple size={32} color={theme.colors.status.success} />
            All Recommended Foods
          </Title>
          <Subtitle>
            Complete list of skin-friendly meals and snacks tailored to your skin health
          </Subtitle>
        </Header>

        {/* Filter by Cuisine Section */}
        <FilterSection data-filter-section>
          <FilterLabel>
            <Filter size={18} />
            Filter by Cuisine
          </FilterLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <SearchInput>
              <Search size={20} />
              <input
                id="cuisine-search"
                name="cuisineSearch"
                type="text"
                placeholder="Search cuisine for AI recommendations (e.g. Indian, Chinese, Mediterranean…)"
                value={cuisineSearch}
                onChange={(e) => setCuisineSearch(e.target.value)}
                aria-label="Filter by cuisine"
              />
              {cuisineSearch && (
                <ClearButton onClick={() => {
                  setCuisineSearch('');
                  setChatGPTFoods([]);
                  setCuisineError(null);
                }}>
                  Clear
                </ClearButton>
              )}
              {loadingCuisine && (
                <span style={{ 
                  fontSize: theme.typography.fontSize.sm, 
                  color: theme.colors.text.secondary,
                  marginLeft: '0.5rem'
                }}>
                  Loading AI recommendations…
                </span>
              )}
              {cuisineError && (
                <span style={{ 
                  fontSize: theme.typography.fontSize.sm, 
                  color: theme.colors.status.error,
                  marginLeft: '0.5rem'
                }}>
                  {cuisineError}
                </span>
              )}
            </SearchInput>
          </div>
        </FilterSection>

        <FoodsGrid>
          {(() => {
            // Cuisine mapping - identify cuisines from food names
            const getCuisine = (foodName: string): string => {
              const name = foodName.toLowerCase();
              
              // Check for Indian cuisine first (most specific)
              if (name.includes('tikka') || name.includes('dal') || name.includes('lentil curry') || name.includes('chickpea curry')) {
                return 'indian';
              }
              
              // Check for Japanese cuisine
              if (name.includes('teriyaki') || name.includes('miso')) {
                return 'japanese';
              }
              
              // Check for Chinese cuisine
              if (name.includes('stir-fry') || name.includes('steamed fish') || name.includes('tofu') && name.includes('brown rice')) {
                return 'chinese';
              }
              
              // Check for French cuisine
              if (name.includes('ratatouille') || name.includes('herb-crusted') && name.includes('chicken')) {
                return 'french';
              }
              
              // Check for Mediterranean cuisine
              if (name.includes('mediterranean') || (name.includes('quinoa bowl') && name.includes('chickpeas'))) {
                return 'mediterranean';
              }
              
              // Check for American cuisine (default for common American dishes)
              if (name.includes('turkey') || name.includes('salmon') || name.includes('chicken') || 
                  name.includes('eggs') || name.includes('meatballs') || name.includes('wrap') || 
                  name.includes('scrambled') || name.includes('grilled') && (name.includes('salmon') || name.includes('chicken'))) {
                // Make sure it's not a diverse cuisine meal
                if (!name.includes('tikka') && !name.includes('teriyaki') && !name.includes('mediterranean') && 
                    !name.includes('dal') && !name.includes('miso') && !name.includes('ratatouille') && 
                    !name.includes('stir-fry') && !name.includes('steamed')) {
                  return 'american';
                }
              }
              
              return 'other';
            };

            // Generate or filter foods by cuisine search
            const filteredFoods = useMemo(() => {
              let baseFoods: DietFoodItem[] = [];
              
              if (!cuisineSearch.trim()) {
                // Return only foods that aren't in upfront list
                baseFoods = displayDietPlan.foodsToEat.filter(food => !upfrontFoodNames.has(food.name));
              } else {
                const searchTerm = cuisineSearch.toLowerCase().trim();
                
                // Comprehensive list of world cuisines - supports EVERY cuisine
                const worldCuisines = [
                  'indian', 'chinese', 'japanese', 'french', 'mediterranean', 'american', 
                  'italian', 'mexican', 'thai', 'korean', 'greek', 'spanish', 'middle eastern',
                  'swedish', 'norwegian', 'danish', 'finnish', 'icelandic', 'german', 'austrian', 'swiss',
                  'belgian', 'dutch', 'polish', 'czech', 'hungarian', 'romanian', 'bulgarian', 'croatian',
                  'serbian', 'slovak', 'slovenian', 'ukrainian', 'russian', 'turkish', 'lebanese', 'syrian',
                  'jordanian', 'israeli', 'palestinian', 'egyptian', 'moroccan', 'tunisian', 'algerian',
                  'ethiopian', 'kenyan', 'south african', 'nigerian', 'ghanaian', 'senegalese',
                  'vietnamese', 'cambodian', 'laotian', 'myanmar', 'malaysian', 'singaporean',
                  'indonesian', 'filipino', 'mongolian', 'taiwanese', 'hong kong',
                  'guatemalan', 'honduran', 'salvadoran', 'nicaraguan', 'costa rican',
                  'panamanian', 'colombian', 'venezuelan', 'ecuadorian', 'peruvian', 'bolivian',
                  'chilean', 'argentine', 'uruguayan', 'paraguayan', 'brazilian',
                  'australian', 'new zealand', 'fijian', 'polynesian', 'hawaiian',
                  'portuguese', 'albanian', 'macedonian', 'georgian', 'armenian', 'azerbaijani',
                  'iranian', 'afghan', 'pakistani', 'bangladeshi', 'sri lankan', 'nepalese', 'bhutanese', 'tibetan'
                ];
                
                // Check if it's a cuisine search (not just a food name search)
                // Accept any search term that could be a cuisine (2+ characters)
                const isCuisineSearch = searchTerm.length >= 2 && (
                  worldCuisines.some(cuisine => searchTerm.includes(cuisine) || cuisine.includes(searchTerm)) ||
                  // Also accept any word that might be a cuisine name
                  /^[a-z]{3,}$/.test(searchTerm)
                );
                
                if (isCuisineSearch && scanAnalysis) {
                  // Filter by Cuisine: ChatGPT only (no static cuisine lists)
                  baseFoods = chatGPTFoods;
                } else {
                  // Fallback to filtering existing foods (excluding upfront foods)
                  baseFoods = displayDietPlan.foodsToEat.filter(food => {
                    // First check if it's in upfront foods
                    if (upfrontFoodNames.has(food.name)) {
                      return false;
                    }
                    const cuisine = getCuisine(food.name);
                    const foodNameLower = food.name.toLowerCase();
                    
                    // Match cuisine type
                    if (cuisine === searchTerm) {
                      return true;
                    }
                    
                    // Match if search term is in cuisine name
                    if (cuisine.includes(searchTerm)) {
                      return true;
                    }
                    
                    // Match if food name contains search term
                    if (foodNameLower.includes(searchTerm)) {
                      return true;
                    }
                    
                    return false;
                  });
                }
              }
              
              // For Pro and Elite plans, ensure exactly 6 meals and 3 snacks (only when no cuisine search)
              if ((tier === 'pro' || tier === 'elite') && !cuisineSearch.trim()) {
                const currentMeals = baseFoods.filter(food => food.type === 'meal' || (!food.type && (food.recipe?.caloriesPerServing ?? 0) >= 300));
                const currentSnacks = baseFoods.filter(food => food.type === 'snack' || (!food.type && (food.recipe?.caloriesPerServing ?? 0) < 300));
                
                // Calculate how many more we need to reach exactly 6 meals and 3 snacks
                const neededMeals = Math.max(0, 6 - currentMeals.length);
                const neededSnacks = Math.max(0, 3 - currentSnacks.length);
                
                if (neededMeals > 0 || neededSnacks > 0) {
                  const additionalFoods = generateAdditionalSkinFriendlyFoods(
                    scanAnalysis,
                    userProfile?.dietaryPreference,
                    neededMeals,
                    neededSnacks
                  );
                  
                  // Filter out duplicates by name
                  const existingNames = new Set(baseFoods.map(f => f.name.toLowerCase()));
                  const uniqueAdditional = additionalFoods.filter(f => !existingNames.has(f.name.toLowerCase()));
                  
                  baseFoods = [...baseFoods, ...uniqueAdditional];
                }
                
                // Now limit to exactly 6 meals and 3 snacks
                const finalMeals = baseFoods.filter(food => food.type === 'meal' || (!food.type && (food.recipe?.caloriesPerServing ?? 0) >= 300));
                const finalSnacks = baseFoods.filter(food => food.type === 'snack' || (!food.type && (food.recipe?.caloriesPerServing ?? 0) < 300));
                
                // Take only first 6 meals and first 3 snacks
                const limitedMeals = finalMeals.slice(0, 6);
                const limitedSnacks = finalSnacks.slice(0, 3);
                
                // Combine and filter baseFoods to only include these limited items
                const limitedFoodNames = new Set([...limitedMeals, ...limitedSnacks].map(f => f.name.toLowerCase()));
                baseFoods = baseFoods.filter(food => limitedFoodNames.has(food.name.toLowerCase()));
              }
              
              return baseFoods;
            }, [displayDietPlan.foodsToEat, cuisineSearch, scanAnalysis, userProfile?.dietaryPreference, upfrontFoodNames, tier, chatGPTFoods]);

            const isMeal = (f: { type?: string; recipe?: { caloriesPerServing?: number } }) =>
              (f.type && String(f.type).toLowerCase().includes('meal')) || (!f.type && (f.recipe?.caloriesPerServing ?? 0) >= 300);
            const isSnack = (f: { type?: string; recipe?: { caloriesPerServing?: number } }) =>
              (f.type && String(f.type).toLowerCase().includes('snack')) || (!f.type && (f.recipe?.caloriesPerServing ?? 0) < 300);
            let meals = filteredFoods.filter(isMeal);
            let snacks = filteredFoods.filter(isSnack);
            
            // For Pro and Elite plans (when no cuisine search), limit to exactly 6 meals and 3 snacks
            if ((tier === 'pro' || tier === 'elite') && !cuisineSearch.trim()) {
              meals = meals.slice(0, 6);
              snacks = snacks.slice(0, 3);
            }
            
            if (meals.length === 0 && snacks.length === 0) {
              return (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '4rem 2rem' }}>
                  <p style={{ fontSize: theme.typography.fontSize.lg, color: theme.colors.text.secondary }}>
                    {loadingCuisine
                      ? `Loading personalized ${cuisineSearch} cuisine recommendations from AI…`
                      : cuisineError
                        ? cuisineError
                        : cuisineSearch.trim()
                          ? `No recommendations for "${cuisineSearch}" right now. Please try again.`
                          : 'No recommended foods.'}
                  </p>
                </div>
              );
            }

            return (
              <>
                {/* Full Meals Section */}
                {meals.length > 0 && (
                  <>
                    <div style={{ gridColumn: '1 / -1', marginTop: meals.length > 0 ? '0' : '0', marginBottom: '1rem' }}>
                      <h3 style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, margin: '0 0 1.5rem 0' }}>
                        Full Meals {cuisineSearch && `(${meals.length} found)`}
                      </h3>
                    </div>
                    {meals.map((food, idx) => (
                      <FoodCard key={`meal-${idx}`}>
                        <FoodName>
                          <Apple size={20} color={theme.colors.status.success} />
                          {food.name}
                        </FoodName>
                        <FoodDescription>{food.description}</FoodDescription>
                        {(() => {
                          const recipe = getRecipeForDisplay(food);
                          const ingList = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
                          const stepList = Array.isArray(recipe.steps) ? recipe.steps : [];
                          return (
                            <RecipeSection>
                              <RecipeSummary>
                                <ChefHat size={16} />
                                View Recipe: {recipe.title}
                                {recipe.caloriesPerServing && ` (${recipe.caloriesPerServing} cal/serving)`}
                              </RecipeSummary>
                              <RecipeContent>
                                {recipe.servings && (
                                  <RecipeInfo>Servings: {recipe.servings}</RecipeInfo>
                                )}
                                <div style={{ marginBottom: '1rem' }}>
                                  <RecipeSectionTitle>📋 Ingredients ({recipe.servings || 2} servings):</RecipeSectionTitle>
                                  <IngredientsList>
                                    {ingList.map((ing, i) => (
                                      <li key={i} style={{ marginBottom: '0.25rem' }}>{typeof ing === 'string' ? ing : String(ing)}</li>
                                    ))}
                                  </IngredientsList>
                                </div>
                                <div>
                                  <RecipeSectionTitle>👨‍🍳 Cooking Steps:</RecipeSectionTitle>
                                  <StepsList>
                                    {stepList.map((step, i) => (
                                      <StepItem key={i} style={{ marginBottom: '0.5rem' }}>{typeof step === 'string' ? step : String(step)}</StepItem>
                                    ))}
                                  </StepsList>
                                </div>
                              </RecipeContent>
                            </RecipeSection>
                          );
                        })()}
                      </FoodCard>
                    ))}
                  </>
                )}
                
                {/* Snacks Section */}
                {snacks.length > 0 && (
                  <>
                    <div style={{ gridColumn: '1 / -1', marginTop: meals.length > 0 ? '2rem' : '0', marginBottom: '1rem' }}>
                      <h3 style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, margin: '0 0 1.5rem 0' }}>
                        Snacks {cuisineSearch && `(${snacks.length} found)`}
                      </h3>
                    </div>
                    {snacks.map((food, idx) => (
                      <FoodCard key={`snack-${idx}`}>
                        <FoodName>
                          <Apple size={20} color={theme.colors.status.success} />
                          {food.name}
                        </FoodName>
                        <FoodDescription>{food.description}</FoodDescription>
                        {(() => {
                          const recipe = getRecipeForDisplay(food);
                          const ingList = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
                          const stepList = Array.isArray(recipe.steps) ? recipe.steps : [];
                          return (
                            <RecipeSection>
                              <RecipeSummary>
                                <ChefHat size={16} />
                                View Recipe: {recipe.title}
                                {recipe.caloriesPerServing && ` (${recipe.caloriesPerServing} cal/serving)`}
                              </RecipeSummary>
                              <RecipeContent>
                                {recipe.servings && (
                                  <RecipeInfo>Servings: {recipe.servings}</RecipeInfo>
                                )}
                                <div style={{ marginBottom: '1rem' }}>
                                  <RecipeSectionTitle>📋 Ingredients ({recipe.servings || 2} servings):</RecipeSectionTitle>
                                  <IngredientsList>
                                    {ingList.map((ing, i) => (
                                      <li key={i} style={{ marginBottom: '0.25rem' }}>{typeof ing === 'string' ? ing : String(ing)}</li>
                                    ))}
                                  </IngredientsList>
                                </div>
                                <div>
                                  <RecipeSectionTitle>👨‍🍳 Cooking Steps:</RecipeSectionTitle>
                                  <StepsList>
                                    {stepList.map((step, i) => (
                                      <StepItem key={i} style={{ marginBottom: '0.5rem' }}>{typeof step === 'string' ? step : String(step)}</StepItem>
                                    ))}
                                  </StepsList>
                                </div>
                              </RecipeContent>
                            </RecipeSection>
                          );
                        })()}
                      </FoodCard>
                    ))}
                  </>
                )}
              </>
            );
          })()}
        </FoodsGrid>
      </Container>
    </PageContainer>
  );
};

export default DietPlanFoodsPage;

















